import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

export interface S3UploadResult {
  s3Key: string;
  fileName: string;
  contentType: string;
  fileSize: number;
}

export interface S3UploadUrlResponse {
  UploadURL: string;
  S3Key: string;
  ContentType: string;
}

export type EntityType =
  | 'vendor'
  | 'client'
  | 'item'
  | 'inventory_adjustment'
  | 'journal_voucher'
  | 'asset';

/**
 * Get the API path for an entity type
 */
function getEntityPath(entityType: EntityType): string {
  switch (entityType) {
    case 'client':
      return 'clients';
    case 'item':
      return 'items';
    case 'inventory_adjustment':
      return 'inventory-adjustments';
    case 'journal_voucher':
      return 'journal-vouchers';
    case 'asset':
      return 'assets';
    case 'vendor':
    default:
      return 'vendors';
  }
}

export interface S3UploadUrlGenerator {
  /**
   * Generate a presigned URL for uploading a file to S3
   * @param entityId - Entity ID (e.g., vendorId, clientId, etc.)
   * @param fileName - Original file name
   * @param contentType - MIME type of the file
   * @returns Observable with upload URL response
   */
  generateUploadUrl(entityId: string | number, fileName: string, contentType: string): Observable<any>;
}

@Injectable({
  providedIn: 'root'
})
export class S3FileUploadService {
  private accountingUrl = environment.accountingUrl;

  constructor(private http: HttpClient) {}

  /**
   * Generate a presigned URL for uploading a file to S3
   * @param entityId - Entity ID (vendorId or clientId)
   * @param fileName - Original file name
   * @param contentType - MIME type of the file
   * @param entityType - Entity type ('vendor' or 'client'), defaults to 'vendor' for backward compatibility
   * @returns Observable with upload URL response
   */
  generateUploadUrl(
    entityId: string | number,
    fileName: string,
    contentType: string,
    entityType: EntityType = 'vendor'
  ): Observable<any> {
    const id = entityId || '0';
    const entityPath = getEntityPath(entityType);
    const url = `${this.accountingUrl}/api/v1/${entityPath}/${id}/files/upload-url`;
    return this.http.post<any>(
      url,
      { file_name: fileName, content_type: contentType },
      { withCredentials: true }
    );
  }

  /**
   * Generate a presigned download URL for a file
   * @param entityId - Entity ID (vendorId or clientId)
   * @param s3Key - S3 key of the file
   * @param entityType - Entity type ('vendor' or 'client'), defaults to 'vendor' for backward compatibility
   * @returns Observable with download URL response
   */
  generateDownloadUrl(
    entityId: string | number,
    s3Key: string,
    entityType: EntityType = 'vendor'
  ): Observable<any> {
    const id = entityId || '0';
    const entityPath = getEntityPath(entityType);
    const url = `${this.accountingUrl}/api/v1/${entityPath}/${id}/files/download-url`;
    return this.http.post<any>(
      url,
      { s3_key: s3Key },
      { withCredentials: true }
    );
  }

  /**
   * Open download URL in a new window
   * @param entityId - Entity ID (vendorId or clientId)
   * @param s3Key - S3 key of the file
   * @param entityType - Entity type ('vendor' or 'client'), defaults to 'vendor' for backward compatibility
   */
  async openDownloadUrl(
    entityId: string | number,
    s3Key: string,
    entityType: EntityType = 'vendor'
  ): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.generateDownloadUrl(entityId, s3Key, entityType)
      );

      if (response?.data?.download_url) {
        window.open(response.data.download_url, '_blank');
      } else {
        throw new Error('No download URL returned from API');
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to fetch download URL';
      throw new Error(errorMessage);
    }
  }

  /**
   * Generate a presigned preview URL for a file (opens inline in browser)
   * @param entityId - Entity ID (vendorId or clientId)
   * @param s3Key - S3 key of the file
   * @param entityType - Entity type ('vendor' or 'client'), defaults to 'vendor' for backward compatibility
   * @returns Observable with preview URL response
   */
  generatePreviewUrl(
    entityId: string | number,
    s3Key: string,
    entityType: EntityType = 'vendor'
  ): Observable<any> {
    const id = entityId || '0';
    const entityPath = getEntityPath(entityType);
    const url = `${this.accountingUrl}/api/v1/${entityPath}/${id}/files/preview-url`;
    return this.http.post<any>(
      url,
      { s3_key: s3Key },
      { withCredentials: true }
    );
  }

  /**
   * Open preview URL in a new window (shows file inline instead of download)
   * @param entityId - Entity ID (vendorId or clientId)
   * @param s3Key - S3 key of the file
   * @param entityType - Entity type ('vendor' or 'client'), defaults to 'vendor' for backward compatibility
   */
  async openPreviewUrl(
    entityId: string | number,
    s3Key: string,
    entityType: EntityType = 'vendor'
  ): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.generatePreviewUrl(entityId, s3Key, entityType)
      );

      if (response?.data?.preview_url) {
        window.open(response.data.preview_url, '_blank');
      } else {
        throw new Error('No preview URL returned from API');
      }
    } catch (error: any) {
      const errorMessage = error?.message || 'Failed to fetch preview URL';
      throw new Error(errorMessage);
    }
  }

  /**
   * Upload a file to S3 using presigned URL
   * This is a generic method that handles the S3 upload flow:
   * 1. Gets presigned URL from backend
   * 2. Uploads file directly to S3
   * 3. Returns the S3 key and metadata
   * 
   * @param file - The file to upload
   * @param entityId - Entity ID (vendorId or clientId)
   * @param entityType - Entity type ('vendor' or 'client'), defaults to 'vendor' for backward compatibility
   * @param uploadUrlGenerator - Optional: Service/function that generates the presigned URL (for backward compatibility)
   * @returns Promise with upload result containing s3Key, fileName, contentType, and fileSize
   */
  async uploadFileToS3(
    file: File,
    entityId: string | number = '0',
    entityType: EntityType = 'vendor',
    uploadUrlGenerator?: S3UploadUrlGenerator
  ): Promise<S3UploadResult> {
    const fileName = file.name;
    const contentType = file.type || 'application/octet-stream';
    const fileSize = file.size;

    // Step 1: Get presigned URL from backend
    let uploadUrlResponse: any;
    
    if (uploadUrlGenerator) {
      // Use provided generator (backward compatibility)
      uploadUrlResponse = await lastValueFrom(
        uploadUrlGenerator.generateUploadUrl(entityId, fileName, contentType)
      );
    } else {
      // Use internal generateUploadUrl method with entity type
      uploadUrlResponse = await lastValueFrom(
        this.generateUploadUrl(entityId, fileName, contentType, entityType)
      );
    }

    if (!uploadUrlResponse?.data?.UploadURL || !uploadUrlResponse?.data?.S3Key) {
      throw new Error('Failed to get upload URL from server');
    }

    const { UploadURL, S3Key, ContentType } = uploadUrlResponse.data as S3UploadUrlResponse;

    // Step 2: Upload file directly to S3 using presigned URL
    // IMPORTANT: Use exact Content-Type from response, PUT method, no extra headers
    const uploadResponse = await fetch(UploadURL, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': ContentType
      }
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`S3 upload failed: ${uploadResponse.status} - ${errorText}`);
    }

    // Step 3: Return the result
    return {
      s3Key: S3Key,
      fileName: fileName,
      contentType: ContentType,
      fileSize: fileSize
    };
  }

  /**
   * Generate a presigned download URL for a file (legacy method for backward compatibility)
   * @param downloadUrlGenerator - Service/function that generates the download URL
   * @param entityId - Entity ID
   * @param s3Key - S3 key of the file
   * @returns Promise with download URL
   */
  async getDownloadUrl(
    downloadUrlGenerator: (entityId: string | number, s3Key: string) => Observable<any>,
    entityId: string | number,
    s3Key: string
  ): Promise<string> {
    const response = await lastValueFrom(
      downloadUrlGenerator(entityId, s3Key)
    );

    if (!response?.data?.download_url) {
      throw new Error('Failed to get download URL from server');
    }

    return response.data.download_url;
  }
}

